// web-v2/src/lib/marketDates.js
//
// When a market row's things actually happened, derived from the row itself.
// WIZARD-FEEDBACK-2026-07-30 wave 3, item 1.
//
// David: "I should see the submission and date ... right there immediately."
// Every date on the ladder was relative and nothing else: "sent 3d ago", in
// every state, with no absolute date anywhere. Recency at a glance is genuinely
// useful and stays; what was missing is the date itself.
//
// THE DECLINED ROW WAS SHOWING THE WRONG DATE, and that is the real defect here.
// declineLine() aged off `updated_at`, so "declined 3d ago" meant the ROW was
// touched three days ago. Re-open the decline dialog to fix a typo in the note
// and the carrier appears to have declined today. state_history is the actual
// record: every transition appends `{ state, at, by }` (submission-markets
// .service.js), so the decline's own timestamp has been sitting there all along.
//
// Pure and dependency-free so tests/market-dates.test.js can import it in node.

// The LAST entry for a state, because a row can revisit one (sent, no_response,
// sent again after a nudge) and the most recent transition is the true one.
function lastEntryAt(market, state) {
    const hist = Array.isArray(market?.state_history) ? market.state_history : [];
    for (let i = hist.length - 1; i >= 0; i -= 1) {
        if (hist[i] && hist[i].state === state && hist[i].at) return hist[i].at;
    }
    return null;
}

// When this submission went to the carrier.
//
// A BROKER-SET DATE WINS. "Set sent date" (SubmissionBuilderPanel) backdates
// quotes.went_to_market_at when the row was logged days after the real send, and
// that correction is a human saying what happened. state_history is the fallback
// and is the honest machine record: when TRACON created the row.
//
// NULL when neither exists. A market with no sent date says so; it does not
// borrow created_at and render today, which would make a row logged this morning
// look like a submission sent this morning.
export function sentAt(market, quote) {
    const broker = quote && quote.went_to_market_at;
    if (broker) return String(broker).slice(0, 10);
    const fromHistory = lastEntryAt(market, 'sent');
    return fromHistory ? String(fromHistory).slice(0, 10) : null;
}

// When the carrier's answer landed (prompt 67 item 2.3). A quoted row led with
// "sent Aug 4 (today)" whatever had happened since, so a row carrying $3,885 was
// still headlining the day it went out.
//
// THE QUOTE'S OWN DATE LEADS, and the market transition is the fallback. That is
// the opposite precedence to sentAt on purpose. `quote_date` is quotes.received_at,
// written when the quote row was created, which is when TRACON actually got the
// answer. The market's `quoted` transition is not always that: the prompt-37
// backfill appended an inferred `quoted` entry to 226 rows on the day it ran, so
// leading with the transition would stamp 2026-08-03 on every historical quote
// as though the carrier had answered that morning.
//
// NULL when neither exists, and the caller falls back to the sent line rather
// than borrowing a date from somewhere else.
export function receivedAt(market, quote) {
    const fromQuote = quote && quote.quote_date;
    if (fromQuote) return String(fromQuote).slice(0, 10);
    const fromHistory = lastEntryAt(market, 'quoted');
    return fromHistory ? String(fromHistory).slice(0, 10) : null;
}

// When the carrier declined, from the decline transition itself. NULL when the
// row is declined but carries no such entry (rows that predate state_history, or
// a state set by something that did not append). The caller says "date unknown"
// rather than substituting the row's last-touched time.
export function declinedAt(market) {
    const at = lastEntryAt(market, 'declined');
    return at ? String(at).slice(0, 10) : null;
}

// Whole days between an ISO date and today, in LOCAL time. Sliced to YMD first
// so a timestamp late in the evening UTC does not read as tomorrow.
export function ageDaysFrom(ymd, todayYmd = new Date().toISOString().slice(0, 10)) {
    if (!ymd) return null;
    const a = Date.parse(`${String(ymd).slice(0, 10)}T00:00:00Z`);
    const b = Date.parse(`${String(todayYmd).slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.round((b - a) / 86400000);
}

// "Jul 29" for this year, "Jul 29 2025" otherwise. Built from the YMD string
// rather than a Date so it cannot shift a day across a timezone.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function shortDate(ymd, todayYmd = new Date().toISOString().slice(0, 10)) {
    if (!ymd) return '';
    const [y, m, d] = String(ymd).slice(0, 10).split('-');
    const mi = Number(m) - 1;
    if (!y || !MONTHS[mi] || !d) return '';
    const label = `${MONTHS[mi]} ${Number(d)}`;
    return y === String(todayYmd).slice(0, 4) ? label : `${label} ${y}`;
}

// The absolute date with the relative one beside it: "Jul 29 (3d ago)". Both,
// because he reads recency at a glance and still needs to know which Tuesday.
// Returns '' for no date at all, which the row renders as an explicit absence.
export function dateWithAge(ymd, todayYmd = new Date().toISOString().slice(0, 10)) {
    if (!ymd) return '';
    const abs = shortDate(ymd, todayYmd);
    const days = ageDaysFrom(ymd, todayYmd);
    if (days == null) return abs;
    if (days === 0) return `${abs} (today)`;
    if (days < 0) return abs; // a backdate set into the future says only the date
    return `${abs} (${days}d ago)`;
}
